import styled from 'styled-components';

export const Panel = styled.div`
  width: min(460px, calc(100vw - 32px));
  max-height: min(78vh, 600px);
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.white};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  box-shadow: 0 20px 56px rgba(48, 70, 89, 0.16);
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  padding: 22px 22px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.aquaSqueeze};
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const Intro = styled.p`
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.BlueBayoux};
`;

export const OnceNote = styled.p`
  margin: 14px 0 0;
  padding: 11px 14px;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.pickledBluewood};
  background: ${({ theme }) => theme.color.white};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.botticelli};
  box-shadow: 0 1px 0 rgba(48, 70, 89, 0.04);
`;

export const CountLine = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px 14px 14px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.color.porcelain};
    border-radius: 8px;
  }
`;

export const Row = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 10px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  border: 1.5px solid
    ${({ $selected, theme }) =>
      $selected ? theme.color.brightTurquoise : theme.color.porcelain};
  background: ${({ $selected, theme }) =>
    $selected ? theme.color.aquaSqueeze : theme.color.white};
  margin-bottom: 8px;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ $selected, theme }) =>
      $selected ? theme.color.brightTurquoise : theme.color.botticelli};
    background: ${({ $selected, theme }) =>
      $selected ? theme.color.aquaSqueeze : theme.color.blackSqueeze};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }
`;

export const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  background: ${({ theme }) => theme.color.porcelain};
`;

/** Same footprint as `Avatar`; initials when there is no logo URL. */
export const InitialsAvatar = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  color: ${({ theme }) => theme.color.brightTurquoise};
  background: linear-gradient(
    145deg,
    ${({ theme }) => theme.color.aquaSqueeze} 0%,
    ${({ theme }) => theme.color.porcelain} 100%
  );
  border: 1px solid ${({ theme }) => theme.color.botticelli};
`;

export const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RowName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.baliHai};
  margin-top: 2px;
`;

export const Check = styled.span`
  font-size: 18px;
  width: 24px;
  text-align: center;
  color: ${({ theme }) => theme.color.brightTurquoise};
`;

export const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 18px 18px;
  border-top: 1px solid ${({ theme }) => theme.color.porcelain};
  justify-content: flex-end;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.color.white};
`;

export const Btn = styled.button<{ $primary?: boolean }>`
  padding: 11px 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: filter 0.15s ease, opacity 0.15s ease, border-color 0.15s ease;
  ${({ $primary, theme }) =>
    $primary
      ? `
    background: ${theme.color.brightTurquoise};
    color: ${theme.color.white};
    box-shadow: 0 2px 8px rgba(51, 171, 167, 0.35);
    &:hover:not(:disabled) { filter: brightness(0.95); }
    &:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
  `
      : `
    background: ${theme.color.white};
    color: ${theme.color.BlueBayoux};
    border: 1.5px solid ${theme.color.porcelain};
    &:hover:not(:disabled) { border-color: ${theme.color.baliHai}; background: ${theme.color.blackSqueeze}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  `}
`;
